import { useState } from "react";
import { ArrowRight, Cpu, Sparkles, TrendingUp, Zap } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-studio-hub-33/artifacts/l67b1y1e_Color%20logo%20-%20no%20background.png";

// Full page mockup with Archivo Black
const ArchivoMockup = () => (
  <div style={{ fontFamily: "'Archivo Black', sans-serif" }}>
    {/* Header */}
    <header className="bg-black border-b-2 border-[#333]">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="flex items-center justify-between h-24">
          <img src={LOGO_URL} alt="Inopsia" className="h-12" />
          <nav className="flex items-center gap-10">
            <span className="text-white text-sm uppercase tracking-wider">Products</span>
            <span className="text-white text-sm uppercase tracking-wider">Philosophy</span>
            <span className="text-white text-sm uppercase tracking-wider">Contact</span>
          </nav>
          <button className="bg-[#F87931] text-black px-8 py-4 text-sm uppercase">
            Get in Touch
          </button>
        </div>
      </div>
    </header>

    {/* Hero */}
    <section className="bg-black py-32 text-center">
      <p className="text-[#F87931] text-base uppercase tracking-[0.3em] mb-8">
        Business & Finance AI Studio
      </p>
      <h1 className="text-7xl lg:text-8xl text-white uppercase mb-4">
        WHERE <span className="text-[#F87931]">AI OPERATORS</span>
      </h1>
      <h1 className="text-7xl lg:text-8xl text-white uppercase mb-10">
        BUILD <span className="text-[#F87931]">AI PRODUCTS</span>
      </h1>
      <p className="text-gray-300 text-lg max-w-3xl mx-auto mb-12" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
        Useful AI, not BS AI. We build agentic, generative, and next-gen AI solutions 
        for business and finance — built by operators, for operators.
      </p>
      <div className="flex justify-center gap-6">
        <button className="bg-[#F87931] text-black px-10 py-5 text-sm uppercase flex items-center gap-3">
          Explore Products <ArrowRight size={20} strokeWidth={3} />
        </button>
        <button className="border-2 border-white text-white px-10 py-5 text-sm uppercase">
          Let's Talk
        </button>
      </div>
    </section>

    {/* Philosophy */}
    <section className="bg-black border-t-2 border-[#333] py-24">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#F87931] text-sm uppercase tracking-[0.2em] mb-4">
              Our Philosophy
            </p>
            <h2 className="text-5xl lg:text-6xl text-white uppercase mb-6">
              From GenAI to<br />
              <span className="text-[#F87931]">Agentic AI</span><br />
              to What's Next
            </h2>
            <p className="text-gray-300 text-lg" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
              We're not here to chase hype. We build AI that solves real problems 
              in business and finance.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {[
              { icon: Cpu, title: "Agentic AI", color: "#F87931" },
              { icon: Sparkles, title: "Generative AI", color: "#22C55E" },
              { icon: TrendingUp, title: "Predictive AI", color: "#3B82F6" },
              { icon: Zap, title: "Next-Gen AI", color: "#A855F7" },
            ].map((item, idx) => (
              <div key={idx} className="bg-[#0A0A0A] border-2 border-[#333] p-6">
                <item.icon style={{ color: item.color }} size={36} strokeWidth={2.5} className="mb-4" />
                <h3 className="text-xl text-white uppercase">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  </div>
);

// Full page mockup with Montserrat Black
const MontserratMockup = () => (
  <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900 }}>
    {/* Header */}
    <header className="bg-black border-b-2 border-[#333]">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="flex items-center justify-between h-24">
          <img src={LOGO_URL} alt="Inopsia" className="h-12" />
          <nav className="flex items-center gap-10">
            <span className="text-white text-sm uppercase tracking-wider" style={{ fontWeight: 800 }}>Products</span>
            <span className="text-white text-sm uppercase tracking-wider" style={{ fontWeight: 800 }}>Philosophy</span>
            <span className="text-white text-sm uppercase tracking-wider" style={{ fontWeight: 800 }}>Contact</span>
          </nav>
          <button className="bg-[#F87931] text-black px-8 py-4 text-sm uppercase" style={{ fontWeight: 800 }}>
            Get in Touch
          </button>
        </div>
      </div>
    </header>

    {/* Hero */}
    <section className="bg-black py-32 text-center">
      <p className="text-[#F87931] text-base uppercase tracking-[0.3em] mb-8" style={{ fontWeight: 800 }}>
        Business & Finance AI Studio
      </p>
      <h1 className="text-7xl lg:text-8xl text-white uppercase mb-4" style={{ letterSpacing: "-0.02em" }}>
        WHERE <span className="text-[#F87931]">AI OPERATORS</span>
      </h1>
      <h1 className="text-7xl lg:text-8xl text-white uppercase mb-10" style={{ letterSpacing: "-0.02em" }}>
        BUILD <span className="text-[#F87931]">AI PRODUCTS</span>
      </h1>
      <p className="text-gray-300 text-lg max-w-3xl mx-auto mb-12" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
        Useful AI, not BS AI. We build agentic, generative, and next-gen AI solutions 
        for business and finance — built by operators, for operators.
      </p>
      <div className="flex justify-center gap-6">
        <button className="bg-[#F87931] text-black px-10 py-5 text-sm uppercase flex items-center gap-3" style={{ fontWeight: 800 }}>
          Explore Products <ArrowRight size={20} strokeWidth={3} />
        </button>
        <button className="border-2 border-white text-white px-10 py-5 text-sm uppercase" style={{ fontWeight: 800 }}>
          Let's Talk
        </button>
      </div>
    </section>

    {/* Philosophy */}
    <section className="bg-black border-t-2 border-[#333] py-24">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#F87931] text-sm uppercase tracking-[0.2em] mb-4" style={{ fontWeight: 800 }}>
              Our Philosophy
            </p>
            <h2 className="text-5xl lg:text-6xl text-white uppercase mb-6" style={{ letterSpacing: "-0.02em" }}>
              From GenAI to<br />
              <span className="text-[#F87931]">Agentic AI</span><br />
              to What's Next
            </h2>
            <p className="text-gray-300 text-lg" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
              We're not here to chase hype. We build AI that solves real problems 
              in business and finance.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {[
              { icon: Cpu, title: "Agentic AI", color: "#F87931" },
              { icon: Sparkles, title: "Generative AI", color: "#22C55E" },
              { icon: TrendingUp, title: "Predictive AI", color: "#3B82F6" },
              { icon: Zap, title: "Next-Gen AI", color: "#A855F7" },
            ].map((item, idx) => (
              <div key={idx} className="bg-[#0A0A0A] border-2 border-[#333] p-6">
                <item.icon style={{ color: item.color }} size={36} strokeWidth={2.5} className="mb-4" />
                <h3 className="text-xl text-white uppercase" style={{ fontWeight: 800 }}>{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default function FontFullMockups() {
  const [activeFont, setActiveFont] = useState("archivo");

  return (
    <>
      {/* Load Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Archivo+Black&family=Montserrat:wght@800;900&family=Inter:wght@500;600&display=swap"
        rel="stylesheet"
      />
      
      <div className="min-h-screen bg-black">
        {/* Selector Bar */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-[#F87931] py-4">
          <div className="max-w-[1400px] mx-auto px-8 flex items-center justify-between">
            <span className="text-black font-bold uppercase text-sm">Full Page Font Preview</span>
            <div className="flex gap-4">
              <button
                onClick={() => setActiveFont("archivo")}
                className={`px-6 py-2 text-sm font-bold uppercase ${
                  activeFont === "archivo" 
                    ? "bg-black text-white" 
                    : "bg-white/20 text-black hover:bg-white/30"
                }`}
              >
                Option 2: Archivo Black
              </button>
              <button
                onClick={() => setActiveFont("montserrat")}
                className={`px-6 py-2 text-sm font-bold uppercase ${
                  activeFont === "montserrat" 
                    ? "bg-black text-white" 
                    : "bg-white/20 text-black hover:bg-white/30"
                }`}
              >
                Option 4: Montserrat Black
              </button>
            </div>
            <a href="/fonts" className="text-black text-sm font-bold hover:underline">
              ← Back to Options
            </a>
          </div>
        </div>

        {/* Mockup Content */}
        <div className="pt-16">
          {activeFont === "archivo" ? <ArchivoMockup /> : <MontserratMockup />}
        </div>
      </div>
    </>
  );
}
